import { TCFSection } from './types';

/**
 * Section 介绍文案接口
 */
export interface SectionIntro {
  sectionId: string;
  title: string;
  description: string;
  tips?: string[];
  duration?: number;
}

/**
 * TCF 听力理解介绍
 */
const TCF_LISTENING_INTRO: SectionIntro = {
  sectionId: TCFSection.LISTENING,
  title: '听力理解 (Compréhension orale)',
  description: 'TCF 听力理解部分测试您对法语口语的理解能力。这部分包含多种类型的听力材料，如对话、访谈、新闻广播等，涵盖日常生活、工作、学习等不同场景。',
  duration: 25,
  tips: [
    '仔细听音频，注意关键词和语调变化',
    '在播放音频前，先快速浏览题目，了解需要关注的信息',
    '注意区分相似发音的词汇，避免混淆',
    '如果第一次没听清楚，可以重复播放（练习模式下）',
    '关注对话者的语气和情感色彩，有助于理解语境',
  ],
};

/**
 * TCF 语言结构介绍
 */
const TCF_LANGUAGE_STRUCTURES_INTRO: SectionIntro = {
  sectionId: TCFSection.LANGUAGE_STRUCTURES,
  title: '语言结构 (Maîtrise des structures de la langue)',
  description: 'TCF 语言结构部分评估您的语法和词汇掌握程度。题目涵盖时态、语态、词性变化、介词使用、固定搭配等语法知识点，以及词汇的准确性和丰富性。',
  duration: 15,
  tips: [
    '掌握基础语法规则，特别是时态和语态的用法',
    '注意词汇的性数配合，确保形容词、过去分词等与主语一致',
    '熟悉常用介词和连词的用法',
    '积累固定搭配和常用表达',
    '注意区分相似词汇的细微差别',
    '做题时注意语境，选择最符合语境的答案',
  ],
};

/**
 * TCF 阅读理解介绍
 */
const TCF_READING_INTRO: SectionIntro = {
  sectionId: TCFSection.READING,
  title: '阅读理解 (Compréhension écrite)',
  description: 'TCF 阅读理解部分测试您对法语书面文本的理解能力。阅读材料包括广告、通知、文章、信件等不同类型的文本，内容涉及日常生活、社会文化、学术研究等多个领域。',
  duration: 45,
  tips: [
    '先快速浏览全文，了解文章主题和结构',
    '仔细阅读题目，明确需要查找的信息',
    '注意关键词和同义词替换',
    '理解文章的逻辑关系，如因果关系、转折关系等',
    '注意文章的语气和作者的态度',
    '遇到生词时，尝试通过上下文推断词义',
    '合理分配时间，不要在单个题目上花费过多时间',
  ],
};

/**
 * TCF 书面表达介绍（可选）
 */
const TCF_WRITING_INTRO: SectionIntro = {
  sectionId: TCFSection.WRITING,
  title: '书面表达 (Expression écrite)',
  description:
    'TCF 书面表达为可选科目，通常包含三道 Tâche：短消息/邮件、叙述或说明、议论文式短文。练习模式下每次完成一道任务并获得 AI 评分反馈。',
  duration: 60,
  tips: [
    'Tâche 1 建议 60–120 词：先回应对方请求，再补充地点、时长、价格等实用信息',
    'Tâche 2 / 3 注意段落结构：开头点题、中间展开、结尾收束',
    '使用连接词让逻辑更清晰，但避免堆砌模板句',
    '写完后检查性数配合、时态一致与拼写',
    '先达到最低字数再提交改卷，过短会拉低任务完成度',
  ],
};

/**
 * TCF Section 介绍文案映射
 */
export const TCF_SECTION_INTROS: Record<string, SectionIntro> = {
  [TCFSection.LISTENING]: TCF_LISTENING_INTRO,
  [TCFSection.LANGUAGE_STRUCTURES]: TCF_LANGUAGE_STRUCTURES_INTRO,
  [TCFSection.READING]: TCF_READING_INTRO,
  [TCFSection.WRITING]: TCF_WRITING_INTRO,
};

/**
 * 获取 TCF Section 介绍文案
 */
export function getTCFSectionIntro(sectionId: string): SectionIntro | null {
  return TCF_SECTION_INTROS[sectionId] || null;
}
